// src/components/rcv/DocumentoButton.jsx
import React, { useState } from 'react';
import {
  IconButton,
  Button,
  Dialog,
  Tooltip,
  CircularProgress,
  Badge
} from '@mui/material';
import { Visibility, AttachFile } from '@mui/icons-material';
import { dtedocService } from '../../services/dtedocService';

// Componentes
import VisualizadorDocumento from './VisualizadorDocumento';
import SubirDocumento from './SubirDocumento';

const DocumentoButton = ({
  documentoId,
  movimientoId,
  rutEmisor,
  tipoDoc,
  folio,
  rutEmpresa,
  soloIcono = false,
  color = "default",
  variant = "contained",
  tieneAdjunto = false,
  onDocumentoCreado
}) => {
  // Estados
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tieneDocumento, setTieneDocumento] = useState(false);
  const [documentoEncontrado, setDocumentoEncontrado] = useState(null);

  const handleClick = async () => {
    setLoading(true);
    
    try {
      let documento = null;
      
      // Buscar documento según los parámetros disponibles
      if (documentoId) {
        documento = await dtedocService.getDocumento(documentoId);
      } else if (movimientoId) {
        const docs = await dtedocService.getDocumentosByMovimiento(movimientoId);
        if (docs && docs.length > 0) {
          documento = docs[0];
        }
      } else if (rutEmisor && tipoDoc && folio) {
        const docs = await dtedocService.getDocumentosByRutTipoFolio({
          rutEmisor, tipoDoc, folio
        });
        if (docs && docs.length > 0) {
          documento = docs[0];
        }
      }
      
      // Guardar resultado de la búsqueda
      if (documento) {
        setDocumentoEncontrado(documento);
        setTieneDocumento(true);
      } else {
        setDocumentoEncontrado(null);
        setTieneDocumento(false);
      }
      
      // Abrir el diálogo
      setOpen(true);
    } catch (error) {
      console.error("Error al verificar documento:", error);
      // Si hay error, asumimos que no existe documento
      setTieneDocumento(false);
      setDocumentoEncontrado(null);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };
  
  const handleDocumentoCreado = (nuevoDocumento) => {
    // Actualizar estado local
    setDocumentoEncontrado(nuevoDocumento);
    setTieneDocumento(true);
    
    // Notificar al componente padre si es necesario
    if (onDocumentoCreado) {
      onDocumentoCreado(nuevoDocumento);
    }
  };

  // Si no hay datos para buscar el documento, no renderizamos el botón
  if (!documentoId && !movimientoId && !(rutEmisor && tipoDoc && folio)) {
    if (soloIcono) {
      return (
        <Tooltip title="Sin datos para buscar documento">
          <span>
            <IconButton size="small" disabled>
              <Visibility fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }
    return null;
  }

  return (
    <>
      {soloIcono ? (
        <Tooltip title={tieneAdjunto ? "Ver adjunto" : "Subir documento"}>
          <IconButton
            size="small"
            onClick={handleClick}
            color={tieneAdjunto ? "primary" : color}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={18} />
            ) : tieneAdjunto ? (
              <Badge
                badgeContent={<AttachFile sx={{ fontSize: 9 }} />}
                color="primary"
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ '& .MuiBadge-badge': { minWidth: 14, height: 14, p: 0 } }}
              >
                <Visibility fontSize="small" />
              </Badge>
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant={variant}
          color={color}
          onClick={handleClick}
          startIcon={loading ? <CircularProgress size={20} /> : <Visibility />}
          size="small"
          disabled={loading}
        >
          {loading ? 'Verificando...' : 'Documento'}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        {tieneDocumento ? (
          // Si hay documento, mostrar el visualizador
          <VisualizadorDocumento 
            documento={documentoEncontrado}
            onClose={handleClose}
          />
        ) : (
          // Si no hay documento, mostrar el formulario de subida
          <SubirDocumento
            rutEmisor={rutEmisor}
            tipoDoc={tipoDoc}
            folio={folio}
            movimientoId={movimientoId}
            rutEmpresa={rutEmpresa}
            onClose={handleClose}
            onDocumentoCreado={handleDocumentoCreado}
          />
        )}
      </Dialog>
    </>
  );
};

export default DocumentoButton;